/**
 * Shown when the API refuses to answer without a principal.
 *
 * Reads differently depending on WHY: a 401 means sign in, and anything else
 * means the server is not answering - which is a different problem and a
 * different thing to do about it.
 *
 * The 'not answering' half is a dead end by construction: every screen that
 * could do anything about it is behind the call that just failed, so whatever
 * this page offers is the whole of what the person can do. It therefore says
 * what the SERVER said - `underlying` is the SQLite error, and 'no such table'
 * and 'unable to open database file' are different problems wearing the same
 * sentence - and it carries the suggestion box, prefilled with the diagnosis.
 * Without that, being stuck here means being unable to report being stuck
 * here, which is exactly how the last one reached us: over Slack, second hand.
 */

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { ApiError, signInUrl } from '../api'
import SuggestionBox from '../components/common/SuggestionBox'

export default function SignIn({ error, onRetry }: { error: ApiError; onRetry: () => void }) {
  const needsSignIn = error.status === 401

  // Written for the reporter, because they cannot see any of it: the page they
  // are looking at has one sentence on it and the useful part is what the
  // server said underneath.
  const report = [
    'I cannot get past the BEST error page - nothing else will load.',
    '',
    `What BEST showed: ${error.message}`,
    error.underlying ? `Underneath: ${error.underlying}` : '',
    `Status: ${error.status}`,
  ]
    .filter(Boolean)
    .join('\n')

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 520, width: '100%' }}>
        <Stack spacing={2}>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '0.06em' }}>
            BEST
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Baseline Echelon SysML Tool
          </Typography>

          {needsSignIn ? (
            <>
              <Typography variant="body1">
                Sign in with your normal GitLab account. BEST never sees your password, and
                what you may do comes from your membership of the BEST project.
              </Typography>
              <Button variant="contained" size="large" href={signInUrl()}>
                Sign in with GitLab
              </Button>
            </>
          ) : (
            <>
              <Alert severity="error">{error.message}</Alert>
              {error.underlying ? (
                <Typography variant="body2" color="text.secondary" component="div">
                  The server said:{' '}
                  <Box
                    component="code"
                    sx={{ wordBreak: 'break-word', fontSize: '0.8125rem' }}
                  >
                    {error.underlying}
                  </Box>
                </Typography>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                If the database has not been set up yet, run <code>best db upgrade</code> and
                then <code>best db ingest</code>.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={onRetry}>
                  Try again
                </Button>
                {/* Hides itself when feedback cannot be filed - which includes
                    having no session, so it never appears next to a sign-in
                    button it could not work beside. */}
                <SuggestionBox
                  trigger="button"
                  label="Report this"
                  prefill={{
                    kind: 'bug',
                    title: `BEST will not load: ${error.message}`.slice(0, 200),
                    detail: report,
                  }}
                />
              </Stack>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  )
}
